import React, { useMemo, useState } from "react";
import cs from "classnames";
import styles from "./styles.module.scss";
import { OrderStatusBadge, OrderStatusDropdown, TONE } from "../OrderTimeline";
import { DataTable } from "../DataTable";
import { DataTableColumn } from "../DataTable/types";
import useIsPhone from "../../../hooks/useIsPhone";
import {
  AppStoreIcon,
  ArrowNarrowRightIcon,
  BookmarkIcon,
  GoogleStoreIcon,
  OpenArrowIcon,
} from "./icons";
import {
  MyOrderRow,
  MyOrdersSortKey,
  MyOrdersSortState,
  MyOrdersStatus,
  MyOrdersStore,
  MyOrdersTableProps,
} from "./types";

// Lifecycle position used when sorting by status.
const STATUS_RANK: Record<MyOrdersStatus, number> = {
  moderation: 0,
  launch: 1,
  active: 2,
  paused: 3,
  done: 4,
  canceled: 5,
};

/** ISO date → DD.MM (the timeline shows no year). */
const formatShortDate = (iso: string | null): string => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}.${mm}`;
};

const clampPercent = (n: number): number =>
  Math.max(0, Math.min(100, Math.round(n)));

// Services whose name isn't just the capitalized key.
const SERVICE_LABELS: Record<string, string> = {
  keywordspro: "Keyword PRO",
};

// Prod names services capitalized ("Keywords", "Direct", "Ratings").
const formatService = (service: string): string =>
  SERVICE_LABELS[service.toLowerCase()] ??
  service.charAt(0).toUpperCase() + service.slice(1).toLowerCase();

const sortValue = (order: MyOrderRow, key: MyOrdersSortKey) => {
  switch (key) {
    case "order": {
      const n = Number(order.orderNumber);
      return Number.isNaN(n) ? String(order.orderNumber).toLowerCase() : n;
    }
    case "app":
      return order.appName ? order.appName.toLowerCase() : null;
    case "store":
      return order.store;
    case "country":
      return order.country ? order.country.toLowerCase() : null;
    case "service":
      return order.service ? order.service.toLowerCase() : null;
    case "progress":
      return clampPercent(order.progress);
    case "status":
      return STATUS_RANK[order.status];
    case "budget":
      return order.budget;
    default:
      return null;
  }
};

// Stable sort; rows with no value always sink to the bottom, both directions.
const sortOrders = (
  orders: MyOrderRow[],
  sort: NonNullable<MyOrdersSortState>,
): MyOrderRow[] => {
  const dir = sort.direction === "asc" ? 1 : -1;
  return orders
    .map((order, index) => ({ order, index }))
    .sort((a, b) => {
      const va = sortValue(a.order, sort.key);
      const vb = sortValue(b.order, sort.key);
      if (va === null && vb === null) return a.index - b.index;
      if (va === null) return 1;
      if (vb === null) return -1;
      const d =
        typeof va === "string"
          ? va.localeCompare(vb as string)
          : (va as number) - (vb as number);
      return d ? d * dir : a.index - b.index;
    })
    .map(({ order }) => order);
};

const matchesFilters = (
  order: MyOrderRow,
  query: string,
  storeFilter: MyOrdersStore | "all",
  statusFilter: MyOrdersStatus | "all",
): boolean => {
  if (storeFilter !== "all" && order.store !== storeFilter) return false;
  if (statusFilter !== "all" && order.status !== statusFilter) return false;
  const q = query.trim().toLowerCase();
  if (
    q &&
    !order.appName.toLowerCase().includes(q) &&
    !String(order.orderNumber).toLowerCase().includes(q)
  ) {
    return false;
  }
  return true;
};

const StoreGlyph: React.FC<{ store: MyOrdersStore }> = ({ store }) =>
  store === "apple" ? (
    <AppStoreIcon className={styles.storeGlyph} />
  ) : (
    <GoogleStoreIcon className={styles.storeGlyph} />
  );

const AppIcon: React.FC<{ iconUrl?: string; store: MyOrdersStore }> = ({
  iconUrl,
  store,
}) => {
  const [failed, setFailed] = useState(false);
  return iconUrl && !failed ? (
    <span className={styles.appIcon}>
      <img src={iconUrl} alt="" onError={() => setFailed(true)} />
    </span>
  ) : (
    <span className={cs(styles.appIcon, styles.appIconFallback)}>
      <StoreGlyph store={store} />
    </span>
  );
};

/**
 * My Orders table, drawn by the DS DataTable as the Apple Ads and admin
 * tables are: sticky header, hairline rows, columns that stretch with the
 * card and that the user resizes and reorders (kept under `layoutId`).
 * Status tones reuse the OrderTimeline .tone-* atoms. Sorting and favorites
 * are controlled-with-uncontrolled-fallback, as in MyAppsTable: pass
 * `sort`/`onSortChange` and `favoriteIds`/`onFavoriteChange` from a container
 * (prod), or omit them and the table manages its own state (sandbox).
 */
export const MyOrdersTable: React.FC<MyOrdersTableProps> = ({
  orders,
  sort,
  defaultSort = null,
  onSortChange,
  clientSort = true,
  favoriteIds,
  defaultFavoriteIds = [],
  onFavoriteChange,
  query = "",
  storeFilter = "all",
  statusFilter = "all",
  onRowClick,
  onOpen,
  onStatusAction,
  renderStatus,
  renderReport,
  emptyText = "No orders found",
  layoutId = "my-orders",
  footer,
  className,
}) => {
  // A phone's card has no room to aim at the open arrow: the card opens.
  const isPhone = useIsPhone();
  const [innerSort, setInnerSort] = useState<MyOrdersSortState>(defaultSort);
  const sortState = sort !== undefined ? sort : innerSort;

  const [innerFavorites, setInnerFavorites] =
    useState<string[]>(defaultFavoriteIds);
  const favorites = favoriteIds !== undefined ? favoriteIds : innerFavorites;
  const favoriteSet = useMemo(() => new Set(favorites), [favorites]);

  const changeSort = (next: MyOrdersSortState) => {
    if (sort === undefined) setInnerSort(next);
    onSortChange?.(next);
  };

  const toggleFavorite = (id: string) => {
    const next = favoriteSet.has(id)
      ? favorites.filter((x) => x !== id)
      : [...favorites, id];
    if (favoriteIds === undefined) setInnerFavorites(next);
    onFavoriteChange?.(next);
  };

  const visibleOrders = useMemo(() => {
    const filtered = orders.filter((order) =>
      matchesFilters(order, query, storeFilter, statusFilter),
    );
    return clientSort && sortState ? sortOrders(filtered, sortState) : filtered;
  }, [orders, query, storeFilter, statusFilter, clientSort, sortState]);

  // Widths are the proportions of a ~1020px table: wider, every column grows
  // with the card; narrower, the table scrolls with Order pinned.
  const columns: DataTableColumn<MyOrderRow>[] = [
    {
      key: "order",
      mobile: "title",
      title: "Order",
      width: 104,
      fixed: true,
      sortable: true,
      render: (order) => {
        const favorite = favoriteSet.has(order.id);
        return (
          <span className={styles.orderCell}>
            <button
              type="button"
              className={cs(styles.favoriteBtn, {
                [styles.favoriteOn]: favorite,
              })}
              aria-label={`${favorite ? "Remove" : "Add"} order ${order.orderNumber} ${favorite ? "from" : "to"} favorites`}
              aria-pressed={favorite}
              onClick={(e) => {
                e.stopPropagation();
                toggleFavorite(order.id);
              }}
            >
              <BookmarkIcon className={styles.favoriteIcon} />
            </button>
            {order.orderNumber}
          </span>
        );
      },
    },
    {
      key: "app",
      mobile: "full",
      title: "App",
      width: 172,
      sortable: true,
      render: (order) => (
        <span className={styles.appInfo}>
          <AppIcon iconUrl={order.appIconUrl} store={order.store} />
          <span className={styles.appName} title={order.appName}>
            {order.appName}
          </span>
        </span>
      ),
    },
    {
      key: "store",
      title: "Store",
      mobile: "header",
      width: 80,
      align: "center",
      sortable: true,
      render: (order) => (
        <span
          className={styles.storeBadge}
          title={order.store === "apple" ? "App Store" : "Google Play"}
        >
          <StoreGlyph store={order.store} />
        </span>
      ),
    },
    {
      key: "country",
      title: "Country",
      width: 120,
      sortable: true,
      render: (order) => order.country || "-",
    },
    {
      key: "service",
      title: "Service",
      width: 112,
      sortable: true,
      render: (order) => formatService(order.service),
    },
    {
      key: "progress",
      mobile: "full",
      title: "Progress",
      width: 152,
      sortable: true,
      render: (order) => {
        const percent = clampPercent(order.progress);
        return (
          <div className={cs(styles.progressBox, TONE[order.status])}>
            <div className={styles.dates}>
              <span className={styles.date}>
                {formatShortDate(order.startDate)}
              </span>
              <span className={styles.datesTrack}>
                <span className={styles.dot} />
                <span className={styles.dashes} />
                <ArrowNarrowRightIcon className={styles.datesArrow} />
                <span className={styles.dashes} />
                <span className={styles.dot} />
              </span>
              <span className={cs(styles.date, styles.dateEnd)}>
                {formatShortDate(order.endDate)}
              </span>
            </div>
            <div className={styles.progressBar}>
              <span
                className={styles.progressFill}
                style={{ width: `${percent}%` }}
              />
              <span className={styles.progressText}>{percent} %</span>
            </div>
          </div>
        );
      },
    },
    {
      key: "status",
      mobile: "header",
      title: "Status",
      width: 132,
      sortable: true,
      stopRowClick: true,
      // The status menu floats below the badge and must not be clipped.
      className: styles.colStatus,
      // As in prod (MyOrders getStatus): moderation/done/canceled — static
      // badge, the rest — an interactive status dropdown.
      render: (order) =>
        renderStatus ? (
          renderStatus(order)
        ) : order.status === "moderation" ||
          order.status === "done" ||
          order.status === "canceled" ? (
          <OrderStatusBadge
            status={order.status}
            className={styles.statusFlat}
          />
        ) : (
          <OrderStatusDropdown
            status={order.status}
            onSelect={(action) => onStatusAction?.(order, action)}
          />
        ),
    },
    {
      key: "budget",
      title: "Budget",
      width: 96,
      sortable: true,
      render: (order) =>
        order.budget != null ? `$${order.budget.toFixed(2)}` : "-",
    },
    ...(renderReport
      ? [
          {
            key: "report",
            title: "Report",
            mobile: "footer" as const,
            width: 88,
            align: "center" as const,
            locked: true,
            stopRowClick: true,
            render: (order: MyOrderRow) => renderReport(order),
          },
        ]
      : []),
    {
      key: "open",
      mobile: "footer",
      title: "",
      width: 48,
      locked: true,
      align: "right",
      render: (order) =>
        onOpen ? (
          <button
            type="button"
            className={styles.openBtn}
            aria-label={`Open order ${order.orderNumber}`}
            onClick={(e) => {
              e.stopPropagation();
              onOpen(order);
            }}
          >
            <OpenArrowIcon className={styles.openIcon} />
          </button>
        ) : null,
    },
  ];

  return (
    <DataTable
      id={layoutId}
      className={className}
      columns={columns}
      rows={visibleOrders}
      rowKey={(order) => order.id}
      sort={sortState}
      onSortChange={(next) => changeSort(next as MyOrdersSortState)}
      onRowClick={onRowClick ?? (isPhone ? onOpen : undefined)}
      emptyText={emptyText}
      footer={footer}
    />
  );
};

export default MyOrdersTable;
